import { INTERVIEW_MODALITY } from '@kiku/config/constants';
import { z } from 'zod';

import { ATS_PROVIDERS, stageMappingSchema } from '@kiku/integrations/types';

export const JOB_STATUS = ['active', 'archived'] as const;
export const JOB_SETUP_STATUS = ['draft', 'questions_pending', 'ready', 'active'] as const;

export const JOB_APPLICANT_SYNC_STATUS = ['pending', 'syncing', 'synced', 'failed'] as const;
export type JobApplicantSyncStatus = (typeof JOB_APPLICANT_SYNC_STATUS)[number];

export const jobSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  employer: z.string().nullable(),
  location: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  interviewId: z.string().uuid(),
  communicationLanguage: z.string().length(2),
  setupStatus: z.enum(JOB_SETUP_STATUS),
  atsExternalJobId: z.string().nullable(),
  atsProvider: z.enum(ATS_PROVIDERS).nullable(),
  applicantSyncStatus: z.enum(JOB_APPLICANT_SYNC_STATUS).nullable(),
  lastApplicantSyncAt: z.date().nullable(),
  status: z.enum(JOB_STATUS),
  createdAt: z.date(),
  createdBy: z.object({ id: z.string(), name: z.string() })
});

export const jobListItemSchema = jobSchema.extend({
  applicationCount: z.number().int(),
  completedInterviewCount: z.number().int()
});

export type Job = z.infer<typeof jobSchema>;
export type JobStatus = Job['status'];
export type JobSetupStatus = Job['setupStatus'];

export const listJobsParamsSchema = z.object({
  search: z.string().optional().default(''),
  status: z.enum(JOB_STATUS).optional(),
  setupStatus: z.enum(JOB_SETUP_STATUS).optional(),
  page: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1))
    .optional()
    .default(1),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(100))
    .optional()
    .default(10),
  deptId: z.string().uuid().optional()
});

export type ListJobsParams = z.infer<typeof listJobsParamsSchema>;

export const createJobInputSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().min(1),
  employer: z.string().min(1),
  modality: z.array(z.enum(INTERVIEW_MODALITY)).min(1),
  languages: z.array(z.string().length(2)).min(1),
  communicationLanguage: z.string().length(2)
});

export type CreateJobInput = z.infer<typeof createJobInputSchema>;

export const importAtsJobsInputSchema = z.object({
  atsJobIds: z.array(z.string()).min(1),
  employer: z.string().min(1),
  modality: z.array(z.enum(INTERVIEW_MODALITY)).min(1),
  languages: z.array(z.string().length(2)).min(1),
  communicationLanguage: z.string().length(2),
  stageSelections: z.record(z.string(), stageMappingSchema).optional()
});

export type ImportAtsJobsInput = z.infer<typeof importAtsJobsInputSchema>;

export const updateSetupStatusInputSchema = z.object({
  setupStatus: z.enum(JOB_SETUP_STATUS)
});

export type UpdateSetupStatusInput = z.infer<typeof updateSetupStatusInputSchema>;

export const updateJobStatusInputSchema = z.object({
  status: z.enum(JOB_STATUS)
});

export type UpdateJobStatusInput = z.infer<typeof updateJobStatusInputSchema>;

export const bulkActivateInputSchema = z.object({
  jobIds: z.array(z.string().uuid()).min(1)
});

export type BulkActivateInput = z.infer<typeof bulkActivateInputSchema>;

export class JobNotFoundError extends Error {
  constructor(id: string) {
    super(`Job ${id} not found`);
    this.name = 'JobNotFoundError';
  }
}

export class InvalidSetupStatusTransitionError extends Error {
  constructor(from: JobSetupStatus, to: JobSetupStatus) {
    super(`Cannot transition job setup status from '${from}' to '${to}'`);
    this.name = 'InvalidSetupStatusTransitionError';
  }
}

export class JobNotAtsLinkedError extends Error {
  constructor(id: string) {
    super(`Job ${id} is not linked to an ATS`);
    this.name = 'JobNotAtsLinkedError';
  }
}

export class AtsJobNotFoundError extends Error {
  constructor(ids: string[]) {
    super(`ATS jobs not found or not accessible: ${ids.join(', ')}`);
    this.name = 'AtsJobNotFoundError';
  }
}
